import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemePreference } from '@devils-paw/ui';

const STORAGE_KEY = 'devils-paw:theme';

interface ThemeValue {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

function storedPreference(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'dark';
  const value = localStorage.getItem(STORAGE_KEY);
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'dark';
}

function applyPreference(preference: ThemePreference): void {
  if (preference === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.dataset.theme = preference;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setCurrentPreference] = useState<ThemePreference>(storedPreference);

  useEffect(() => applyPreference(preference), [preference]);

  const value = useMemo<ThemeValue>(() => ({
    preference,
    setPreference(nextPreference) {
      localStorage.setItem(STORAGE_KEY, nextPreference);
      applyPreference(nextPreference);
      setCurrentPreference(nextPreference);
    },
  }), [preference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (value === null) throw new Error('useTheme must be used inside ThemeProvider');
  return value;
}
